/**
 * Prop verbs (Phase 3, the prop sandbox). Furniture placed by `props.ts` carries
 * its identity in a glyph override on an archetype tile; this module turns that
 * identity into ACTIONS the player can take on it — starting with FLIP.
 *
 * Pure sim: everything here reads/writes `MapData` only. Sound emission, the
 * event log, and the light-field recompute belong to the caller, which is why
 * an action carries its own `loudness` + `message` instead of firing them.
 *
 * FLIP — shove a waist-high desk/table/counter onto its side. The upright piece
 * is see-over cover (`PropLow`); tipped over, its tabletop stands vertical and
 * becomes a full sight-blocker (`PropTall`), so you trade "I can see over it"
 * for "nobody can see through it". It is loud, and it is one-way: a flipped
 * prop is no longer flippable, so it can't be farmed as a free noise loop.
 *
 * HIDE — climb into a body-sized cabinet/locker and pull the door shut. Unlike
 * FLIP this changes no tiles: it sets `player.concealedAt`, which `awareness.ts`
 * reads to make the player unseeable (an alert guard beside the prop still
 * flushes you out). The player stays on their own tile; moving breaks it.
 *
 * STASH — the same cabinet, with a body at hand: fold the corpse in and shut
 * the door. A stashed body (`guard.stashed`) is out of the world — `corpseWatch`
 * never spots it, so the evidence stops raising alarms.
 *
 * TOPPLE — pull a tall shelf or rack over. It falls into the tile beyond,
 * leaving its own tile clear and a waist-high wreck where it landed: you have
 * opened one route and closed another, very loudly. Refused when the wreck would
 * seal a room off.
 *
 * SHOVE — push a waist-high prop one tile away from you. Cover you can reposition
 * — slower and quieter than flipping, and the only verb that moves cover to
 * where you need it rather than using it where it stands.
 *
 * TAKE / THROW — pick up loose clutter (`PropDecor`) and hurl it across the
 * room. The noise lands where the OBJECT lands, not where you are: the whole
 * point is to buy an investigation somewhere you aren't. The object comes to
 * rest as clutter again, so a thrown decoy can be walked over and re-used.
 */

import { setTile, tileAt, inBounds, type MapData } from '../map';
import { TileType } from '../tiles';
import { CONFIG } from '../../config';

/** Verbs the sandbox understands. */
export type PropVerb = 'flip' | 'hide' | 'take' | 'stash' | 'topple' | 'shove';

/** What the actor brings to the query — things the MAP can't know. A body at
 *  hand turns the cabinet's 'hide' into 'stash': you put the evidence in
 *  instead of yourself. */
export interface PropContext {
  /** A corpse is adjacent to the actor and can be dragged into a prop. */
  bodyAtHand?: boolean;
}

export interface PropAction {
  verb: PropVerb;
  /** Tile the action targets (the prop itself, not the actor). */
  x: number;
  y: number;
  /** Sound budget the action costs, in tile units (see `CONFIG.sound`). */
  loudness: number;
  /** Event-log line for the actor performing it. */
  message: string;
}

/** Glyphs `props.ts` uses for waist-high furniture you can tip over. */
const FLIPPABLE_GLYPHS = new Set(['π', '╤', '╥']); // desk, counter, table

/** The tipped-over glyph — also the marker that a prop is already flipped. */
export const FLIPPED_GLYPH = '╨';

/** Human label per flippable glyph, for the log line. */
const FLIP_LABEL: Record<string, string> = { 'π': 'desk', '╤': 'counter', '╥': 'table' };

/** Glyphs for body-sized furniture you can climb inside. Racks and shelving
 *  (open frames) don't qualify — only the closed cabinet/locker. */
const HIDEABLE_GLYPHS = new Set(['▤']); // cabinet / locker

/** Tall furniture that can be pulled over: racks and shelving, not lockers
 *  (a cabinet is for hiding in, and toppling it would waste that). */
const TOPPLEABLE_GLYPHS = new Set(['▥', '≡', '§']); // rack, shelf, files

/** The wreck a toppled unit leaves — waist-high cover where it landed. */
export const TOPPLED_GLYPH = '╧';

/** Loose clutter light enough to carry off and throw. `props.ts` scatters it as
 *  passable decor, and a thrown object comes to rest as this again. */
export const CLUTTER_GLYPH = '◊';

/** The action available on the prop at (x,y), or null if there isn't one. */
export function propActionAt(map: MapData, x: number, y: number, ctx?: PropContext): PropAction | null {
  if (!inBounds(map, x, y)) return null;
  const tile = tileAt(map, x, y);
  const glyph = map.glyphs[y * map.width + x] ?? '';
  // Waist-high furniture still on its feet → flip it. (A flipped prop is
  // PropTall with the flipped glyph, so it never matches again.)
  if (tile === TileType.PropLow && FLIPPABLE_GLYPHS.has(glyph)) {
    return {
      verb: 'flip',
      x,
      y,
      loudness: CONFIG.prop.flipLoudness,
      message: `You flip the ${FLIP_LABEL[glyph] ?? 'desk'} — a loud crash!`,
    };
  }
  // Body-sized closed furniture → put a body in it, or climb in yourself.
  if (tile === TileType.PropTall && HIDEABLE_GLYPHS.has(glyph)) {
    return ctx?.bodyAtHand
      ? {
          verb: 'stash',
          x,
          y,
          loudness: CONFIG.prop.stashLoudness,
          message: 'You fold the body into the cabinet and shut it.',
        }
      : {
          verb: 'hide',
          x,
          y,
          loudness: CONFIG.prop.hideLoudness,
          message: 'You squeeze into the cabinet and pull it shut.',
        };
  }
  // Wreckage from a toppled unit → shove it along. The only verb that MOVES
  // cover rather than using it where it stands.
  if (tile === TileType.PropLow && glyph === TOPPLED_GLYPH) {
    return {
      verb: 'shove',
      x,
      y,
      loudness: CONFIG.prop.shoveLoudness,
      message: 'You shove the wreckage along the floor.',
    };
  }
  // Tall shelving → pull it over into the space beyond.
  if (tile === TileType.PropTall && TOPPLEABLE_GLYPHS.has(glyph)) {
    return {
      verb: 'topple',
      x,
      y,
      loudness: CONFIG.prop.toppleLoudness,
      message: 'You haul the shelving over — it goes down like a tree.',
    };
  }
  // Loose clutter → pocket it as a throwable.
  if (tile === TileType.PropDecor && glyph === CLUTTER_GLYPH) {
    return {
      verb: 'take',
      x,
      y,
      loudness: CONFIG.prop.takeLoudness,
      message: 'You palm a loose object.',
    };
  }
  return null;
}

/** The first prop action at (x,y) or on a 4-neighbour — what E acts on. The
 *  actor's OWN tile is checked first: clutter is passable, so you can be
 *  standing on the thing you want to pick up. */
export function findPropAction(map: MapData, x: number, y: number, ctx?: PropContext): PropAction | null {
  for (const [dx, dy] of [[0, 0], [1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
    const a = propActionAt(map, x + dx, y + dy, ctx);
    if (a) return a;
  }
  return null;
}

/**
 * Apply an action's effect ON THE MAP. Re-validates the target (the world runs
 * live, so the prop may have been shot to pieces between query and use) and
 * returns whether it took effect — the caller emits the sound/log only if it
 * did. Callers must recompute the light field after a true: a flipped prop is
 * a new light blocker.
 *
 * Verbs that move PEOPLE rather than tiles ('hide', 'stash') return false here;
 * the caller drives those (a still-valid action is confirmed by `propActionAt`).
 */
export function applyPropAction(
  map: MapData,
  action: PropAction,
  ctx?: PropContext,
  /** Where the actor is standing — topple and shove push AWAY from them. */
  from?: { x: number; y: number },
): boolean {
  const fresh = propActionAt(map, action.x, action.y, ctx);
  if (!fresh || fresh.verb !== action.verb) return false;
  const idx = action.y * map.width + action.x;
  if (action.verb === 'topple' || action.verb === 'shove') {
    return pushMass(map, action, from);
  }
  if (action.verb === 'flip') {
    setTile(map, action.x, action.y, TileType.PropTall); // on its side: a full sight-blocker
    map.glyphs[idx] = FLIPPED_GLYPH;
    map.dirtyTiles.add(idx);
    return true;
  }
  if (action.verb === 'take') {
    setTile(map, action.x, action.y, TileType.Floor); // it's in your hand now
    map.dirtyTiles.add(idx);
    return true;
  }
  return false; // 'hide'/'stash' move people, not tiles
}

/**
 * Move a prop one tile away from the actor: TOPPLE drops a tall unit into the
 * space beyond as waist-high wreckage, SHOVE slides a low prop along. Both
 * refuse if the destination isn't clear floor, and both re-check that the room
 * is still walkable afterwards — moving furniture into a doorway to seal a
 * patrol out (or yourself in) is exactly the failure this prevents.
 */
function pushMass(map: MapData, action: PropAction, from?: { x: number; y: number }): boolean {
  if (!from) return false;
  const dx = Math.sign(action.x - from.x);
  const dy = Math.sign(action.y - from.y);
  if (dx === 0 && dy === 0) return false;
  if (dx !== 0 && dy !== 0) return false; // straight pushes only
  const tx = action.x + dx;
  const ty = action.y + dy;
  if (!inBounds(map, tx, ty) || tileAt(map, tx, ty) !== TileType.Floor) return false;

  const wasTile = tileAt(map, action.x, action.y);
  const wasGlyph = map.glyphs[action.y * map.width + action.x] ?? '';
  const landed = action.verb === 'topple' ? TileType.PropLow : wasTile;
  const landedGlyph = action.verb === 'topple' ? TOPPLED_GLYPH : wasGlyph;

  setTile(map, action.x, action.y, TileType.Floor);
  setTile(map, tx, ty, landed);
  map.glyphs[ty * map.width + tx] = landedGlyph;
  if (!stillWalkable(map)) {
    // Put it back — this push would have sealed something off.
    setTile(map, tx, ty, TileType.Floor);
    setTile(map, action.x, action.y, wasTile);
    map.glyphs[action.y * map.width + action.x] = wasGlyph;
    return false;
  }
  map.dirtyTiles.add(action.y * map.width + action.x);
  map.dirtyTiles.add(ty * map.width + tx);
  return true;
}

/** Every floor tile still reachable from the spawn (props are walls). */
function stillWalkable(map: MapData): boolean {
  const seen = new Uint8Array(map.width * map.height);
  const stack = [map.spawn.y * map.width + map.spawn.x];
  const open = (t: TileType) => t === TileType.Floor || t === TileType.PropDecor
    || t === TileType.Door || t === TileType.DoorClosed
    || t === TileType.StairsUp || t === TileType.StairsDown;
  while (stack.length) {
    const i = stack.pop()!;
    if (seen[i]) continue;
    seen[i] = 1;
    const x = i % map.width;
    const y = Math.floor(i / map.width);
    if (!open(tileAt(map, x, y))) continue;
    if (x > 0) stack.push(i - 1);
    if (x < map.width - 1) stack.push(i + 1);
    if (y > 0) stack.push(i - map.width);
    if (y < map.height - 1) stack.push(i + map.width);
  }
  for (let y = 0; y < map.height; y++) {
    for (let x = 0; x < map.width; x++) {
      if (tileAt(map, x, y) === TileType.Floor && !seen[y * map.width + x]) return false;
    }
  }
  return true;
}

/**
 * Where an object thrown from (x,y) along (dx,dy) comes to rest: the last open
 * tile before a blocker, at most `range` tiles out. Returns null if it can't
 * leave the thrower's tile (a wall right in front) — nothing is thrown, so the
 * player doesn't waste the object on a noise at their own feet.
 */
export function throwLanding(
  map: MapData,
  x: number,
  y: number,
  dx: number,
  dy: number,
  range: number,
): { x: number; y: number } | null {
  if (dx === 0 && dy === 0) return null;
  let last: { x: number; y: number } | null = null;
  for (let step = 1; step <= range; step++) {
    const tx = x + dx * step;
    const ty = y + dy * step;
    if (!inBounds(map, tx, ty)) break;
    const t = tileAt(map, tx, ty);
    // Doorways and clutter fly through; anything solid stops the object short.
    if (t !== TileType.Floor && t !== TileType.PropDecor && t !== TileType.Door
      && t !== TileType.StairsUp && t !== TileType.StairsDown) break;
    last = { x: tx, y: ty };
  }
  return last;
}

/** Drop a thrown object at its landing tile so it can be picked up again. */
export function landThrownObject(map: MapData, x: number, y: number): void {
  if (tileAt(map, x, y) !== TileType.Floor) return; // already clutter/doorway — no stacking
  setTile(map, x, y, TileType.PropDecor);
  map.glyphs[y * map.width + x] = CLUTTER_GLYPH;
  map.dirtyTiles.add(y * map.width + x);
}

// ---------------------------------------------------------------------------
// The CHANNEL verb — hold-to-operate, interruptible.
//
// Generic on purpose: sabotaging a service plant, tapping an outlet, hacking a
// terminal and planting a device are all "hold the key, stand still, don't get
// shot". The caller owns WHAT is being channelled (any object identity works as
// the target) and the UI; this owns the timing and the interrupt rules.
// ---------------------------------------------------------------------------

export interface ChannelState {
  /** What is being channelled — identity-compared, so switching targets resets. */
  target: unknown | null;
  /** Seconds accumulated on the current target. */
  progress: number;
}

export interface ChannelInput {
  /** The thing at hand, or null when there's nothing to channel. */
  target: unknown | null;
  /** The channel key is down. */
  held: boolean;
  /** Anything that breaks concentration: moving, dying, a menu opening. */
  interrupted: boolean;
  /** Seconds this channel takes. */
  durationSec: number;
  /** Elapsed seconds this tick. */
  dt: number;
}

export function createChannel(): ChannelState {
  return { target: null, progress: 0 };
}

/** Fraction filled, 0..1 — what a progress bar renders. */
export function channelFraction(state: ChannelState, durationSec: number): number {
  return durationSec <= 0 ? 0 : Math.min(1, state.progress / durationSec);
}

/**
 * Advance a channel by one tick. Returns 'done' on the tick it completes (the
 * caller applies the effect), 'channeling' while it fills, 'idle' otherwise.
 * Releasing the key, moving, or switching target all reset the progress —
 * there is no partial credit.
 */
export function updateChannel(state: ChannelState, input: ChannelInput): 'idle' | 'channeling' | 'done' {
  const { target, held, interrupted, durationSec, dt } = input;
  if (!target || !held || interrupted) {
    state.target = null;
    state.progress = 0;
    return 'idle';
  }
  if (state.target !== target) {
    state.target = target;
    state.progress = 0;
  }
  state.progress += dt;
  if (state.progress >= durationSec) {
    state.target = null;
    state.progress = 0;
    return 'done';
  }
  return 'channeling';
}
